/**
 * SubmittalsDashboard — Autodesk Build-style submittal log + review workflow
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const API = '/technical-submittals';

const STATUS_OPTIONS = ['Draft', 'Submitted', 'Under Review', 'Approved', 'Rejected', 'Closed'];

const STATUS_BADGE = {
  Draft: '#9ca3af',
  Submitted: '#3b82f6',
  'Under Review': '#f59e0b',
  Approved: '#16a34a',
  Rejected: '#ef4444',
  Closed: '#6b7280',
};

const REVIEW_BADGE = {
  Pending: '#9ca3af',
  Approved: '#16a34a',
  'Approved as Noted': '#22c55e',
  'Revise and Resubmit': '#f59e0b',
  Rejected: '#ef4444',
};

const SUBMITTAL_TYPES = [
  'Shop Drawing',
  'Product Data',
  'Sample',
  'Mock-up',
  'Design Data',
  'Test Report',
  'Certificate',
  'O&M Manual',
  'Warranty',
  'Closeout',
  'Other',
];

const REVIEW_STATUSES = ['Pending', 'Approved', 'Approved as Noted', 'Revise and Resubmit', 'Rejected'];

const PRIORITIES = ['Low', 'Normal', 'High'];

const COLUMNS = [
  { key: 'submittal_number', label: '#', sortKey: 'submittal_number' },
  { key: 'title', label: 'Title', sortKey: 'title' },
  { key: 'spec_section', label: 'Spec Section', sortKey: 'spec_section' },
  { key: 'submittal_type', label: 'Type', sortKey: 'submittal_type' },
  { key: 'status', label: 'Status', sortKey: 'status' },
  { key: 'priority', label: 'Priority', sortKey: 'priority' },
  { key: 'ball_in_court', label: 'Ball in Court', sortKey: 'ball_in_court' },
  { key: 'submitted_date', label: 'Submitted Date', sortKey: 'submitted_date' },
  { key: 'required_date', label: 'Required Date', sortKey: 'required_date' },
  { key: 'review_status', label: 'Review Status', sortKey: 'review_status' },
  { key: 'reviewer', label: 'Reviewer', sortKey: 'reviewer' },
];

function formatDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

function toInputDate(val) {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

const emptyCreateForm = () => ({
  title: '',
  spec_section: '',
  submittal_type: 'Shop Drawing',
  priority: 'Normal',
  discipline: '',
  description: '',
  required_date: '',
  status: 'Draft',
});

const emptyDetailForm = () => ({
  title: '',
  description: '',
  spec_section: '',
  submittal_type: 'Shop Drawing',
  status: 'Draft',
  priority: 'Normal',
  discipline: '',
  submitted_by: '',
  submitted_date: '',
  required_date: '',
  received_date: '',
  reviewer: '',
  review_status: 'Pending',
  review_comments: '',
  review_date: '',
  revision: '',
  ball_in_court: '',
});

const SubmittalsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalSubmittals', projectId || 'global', true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortKey, setSortKey] = useState('submittal_number');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(() => new Set());
  const [toast, setToast] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(() => emptyCreateForm());
  const [createFile, setCreateFile] = useState(null);
  const [createDrag, setCreateDrag] = useState(false);
  const [creating, setCreating] = useState(false);

  const [detailId, setDetailId] = useState(null);
  const [detailForm, setDetailForm] = useState(() => emptyDetailForm());
  const [savingDetail, setSavingDetail] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}`);
      if (r.data?.success) setItems(r.data.data || []);
    } catch (err) {
      console.error('Submittals fetch error:', err);
      showToast('Failed to load submittals', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((row) => {
      if (statusFilter !== 'All' && row.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        row.title,
        row.spec_section,
        row.discipline,
        row.reviewer,
        row.ball_in_court,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (sortKey === 'submitted_date' || sortKey === 'required_date') {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        return (ad - bd) * dir;
      }
      if (sortKey === 'submittal_number') {
        const parseNum = (s) => {
          const m = String(s || '').match(/^SUB-(\d+)$/i);
          return m ? parseInt(m[1], 10) : 0;
        };
        return (parseNum(av) - parseNum(bv)) * dir;
      }
      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleRowSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sorted.length && sorted.length) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(sorted.map((r) => r.id)));
  };

  const handleCreate = async () => {
    if (!createForm.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('title', createForm.title.trim());
      if (createForm.spec_section) formData.append('spec_section', createForm.spec_section);
      formData.append('submittal_type', createForm.submittal_type);
      formData.append('priority', createForm.priority);
      if (createForm.discipline) formData.append('discipline', createForm.discipline);
      if (createForm.description) formData.append('description', createForm.description);
      if (createForm.required_date) formData.append('required_date', createForm.required_date);
      formData.append('status', createForm.status);
      if (createFile) formData.append('file', createFile);

      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}`, formData, {});
      if (r.data?.success) {
        setShowCreate(false);
        setCreateForm(emptyCreateForm());
        setCreateFile(null);
        fetchItems();
        showToast('Submittal created');
      } else {
        showToast(r.data?.message || 'Create failed', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to create submittal', 'error');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = (row) => {
    setDetailId(row.id);
    setDetailForm({
      ...emptyDetailForm(),
      title: row.title || '',
      description: row.description || '',
      spec_section: row.spec_section || '',
      submittal_type: row.submittal_type || 'Shop Drawing',
      status: row.status || 'Draft',
      priority: row.priority || 'Normal',
      discipline: row.discipline || '',
      submitted_by: row.submitted_by || '',
      submitted_date: toInputDate(row.submitted_date),
      required_date: toInputDate(row.required_date),
      received_date: toInputDate(row.received_date),
      reviewer: row.reviewer || '',
      review_status: row.review_status || 'Pending',
      review_comments: row.review_comments || '',
      review_date: toInputDate(row.review_date),
      revision: row.revision != null ? String(row.revision) : '',
      ball_in_court: row.ball_in_court || '',
    });
  };

  const handleSaveDetail = async () => {
    if (!detailId) return;
    setSavingDetail(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const body = {
        title: detailForm.title,
        description: detailForm.description || null,
        spec_section: detailForm.spec_section || null,
        submittal_type: detailForm.submittal_type,
        status: detailForm.status,
        priority: detailForm.priority,
        discipline: detailForm.discipline || null,
        submitted_by: detailForm.submitted_by || null,
        submitted_date: detailForm.submitted_date || null,
        required_date: detailForm.required_date || null,
        received_date: detailForm.received_date || null,
        reviewer: detailForm.reviewer || null,
        review_status: detailForm.review_status,
        review_comments: detailForm.review_comments || null,
        review_date: today,
        revision: detailForm.revision || null,
        ball_in_court: detailForm.ball_in_court || null,
      };

      const r = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/submittal/${detailId}`, body);
      if (r.data?.success) {
        setDetailForm((f) => ({ ...f, review_date: today }));
        fetchItems();
        showToast('Saved');
      } else {
        showToast(r.data?.message || 'Save failed', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to save', 'error');
    } finally {
      setSavingDetail(false);
    }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm('Delete this submittal?')) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/submittal/${id}`);
      if (r.data?.success) {
        if (detailId === id) setDetailId(null);
        fetchItems();
        showToast('Deleted');
      }
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const handleBatchDelete = async () => {
    if (!selected.size) return;
    const count = selected.size;
    if (!window.confirm(`Delete ${count} selected submittal(s)?`)) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/batch-delete`, {
        ids: Array.from(selected),
      });
      if (r.data?.success) {
        setSelected(new Set());
        setDetailId(null);
        fetchItems();
        showToast(`Deleted ${r.data.deleted ?? count} submittal(s)`);
      }
    } catch (err) {
      showToast('Batch delete failed', 'error');
    }
  };

  const handleViewFile = async (submittalId) => {
    try {
      const r = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${API}/submittal/${submittalId}/file`,
        null,
        { responseType: 'blob' }
      );
      const blob = r.data;
      if (!(blob instanceof Blob) || blob.size === 0) {
        showToast('No file available', 'error');
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error(err);
      showToast('Could not open file', 'error');
    }
  };

  const baseFont = { fontFamily: 'system-ui, sans-serif' };

  return (
    <div style={{ ...baseFont, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, flex: '1 1 auto' }}>Submittals</h2>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        <input
          type="search"
          placeholder="Search title, spec, discipline, reviewer, ball in court…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            minWidth: 240,
            flex: '1 1 200px',
            padding: '8px 10px',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: 14,
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 14, background: '#fff' }}
        >
          <option value="All">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={handleBatchDelete}
            style={{
              padding: '8px 14px',
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            Delete selected ({selected.size})
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setCreateForm(emptyCreateForm());
            setCreateFile(null);
            setShowCreate(true);
          }}
          style={{
            padding: '8px 14px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          Create Submittal
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', width: 36 }}>
                <input
                  type="checkbox"
                  checked={sorted.length > 0 && selected.size === sorted.length}
                  onChange={toggleSelectAll}
                />
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.sortKey)}
                  style={{
                    padding: '10px 8px',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: sortKey === col.sortKey ? '#2563eb' : '#374151',
                  }}
                >
                  {col.label}
                  {sortKey === col.sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              <th style={{ padding: '10px 8px', textAlign: 'left' }}>File</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={COLUMNS.length + 2} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 2} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                  No submittals match your filters.
                </td>
              </tr>
            )}
            {!loading &&
              sorted.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => openDetail(row)}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <td style={{ padding: '8px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleRowSelect(row.id)}
                    />
                  </td>
                  <td style={{ padding: '8px', fontWeight: 500 }}>{row.submittal_number || '—'}</td>
                  <td style={{ padding: '8px', maxWidth: 220 }}>{row.title || '—'}</td>
                  <td style={{ padding: '8px' }}>{row.spec_section || '—'}</td>
                  <td style={{ padding: '8px' }}>{row.submittal_type || '—'}</td>
                  <td style={{ padding: '8px' }}>
                    {row.status ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 500,
                          color: '#fff',
                          background: STATUS_BADGE[row.status] || '#9ca3af',
                        }}
                      >
                        {row.status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: '8px' }}>{row.priority || '—'}</td>
                  <td style={{ padding: '8px' }}>{row.ball_in_court || '—'}</td>
                  <td style={{ padding: '8px' }}>{formatDate(row.submitted_date)}</td>
                  <td style={{ padding: '8px' }}>{formatDate(row.required_date)}</td>
                  <td style={{ padding: '8px' }}>
                    {row.review_status ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 500,
                          color: '#fff',
                          background: REVIEW_BADGE[row.review_status] || '#9ca3af',
                        }}
                      >
                        {row.review_status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: '8px' }}>{row.reviewer || '—'}</td>
                  <td style={{ padding: '8px' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => handleViewFile(row.id)}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      View file
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '12px 16px',
            borderRadius: 8,
            background: toast.type === 'error' ? '#fef2f2' : '#ecfdf5',
            color: toast.type === 'error' ? '#991b1b' : '#065f46',
            border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#a7f3d0'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 10001,
            fontSize: 14,
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 16,
          }}
          onClick={() => !creating && setShowCreate(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              maxWidth: 520,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 16 }}>
              Create Submittal
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Title *
                <input
                  value={createForm.title}
                  onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Spec section
                <input
                  value={createForm.spec_section}
                  onChange={(e) => setCreateForm((f) => ({ ...f, spec_section: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Type
                  <select
                    value={createForm.submittal_type}
                    onChange={(e) => setCreateForm((f) => ({ ...f, submittal_type: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    {SUBMITTAL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Priority
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Discipline
                <input
                  value={createForm.discipline}
                  onChange={(e) => setCreateForm((f) => ({ ...f, discipline: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Description
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Required date
                <input
                  type="date"
                  value={createForm.required_date}
                  onChange={(e) => setCreateForm((f) => ({ ...f, required_date: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Status
                <select
                  value={createForm.status}
                  onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <div
                onDragEnter={() => setCreateDrag(true)}
                onDragLeave={() => setCreateDrag(false)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setCreateDrag(true);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setCreateDrag(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) setCreateFile(f);
                }}
                style={{
                  border: `2px dashed ${createDrag ? '#2563eb' : '#d1d5db'}`,
                  borderRadius: 8,
                  padding: 24,
                  textAlign: 'center',
                  background: createDrag ? '#eff6ff' : '#f9fafb',
                  cursor: 'pointer',
                }}
                onClick={() => document.getElementById('submittal-create-file')?.click()}
              >
                <input
                  id="submittal-create-file"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                />
                <div style={{ fontSize: 14, color: '#374151' }}>Drag & drop a file here, or click to browse</div>
                {createFile && <div style={{ marginTop: 8, fontSize: 13, color: '#2563eb' }}>{createFile.name}</div>}
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                disabled={creating}
                onClick={() => setShowCreate(false)}
                style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={creating}
                onClick={handleCreate}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  cursor: creating ? 'wait' : 'pointer',
                  fontWeight: 500,
                }}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail / edit modal */}
      {detailId != null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 16,
          }}
          onClick={() => !savingDetail && setDetailId(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              maxWidth: 640,
              width: '100%',
              maxHeight: '92vh',
              overflow: 'auto',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>Edit Submittal</span>
              <button
                type="button"
                onClick={() => setDetailId(null)}
                style={{ border: 'none', background: 'transparent', fontSize: 20, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Submittal #{' '}
                <strong style={{ color: '#111' }}>
                  {items.find((x) => x.id === detailId)?.submittal_number || '—'}
                </strong>
              </div>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Title *
                <input
                  value={detailForm.title}
                  onChange={(e) => setDetailForm((f) => ({ ...f, title: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Description
                <textarea
                  value={detailForm.description}
                  onChange={(e) => setDetailForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Spec section
                  <input
                    value={detailForm.spec_section}
                    onChange={(e) => setDetailForm((f) => ({ ...f, spec_section: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Type
                  <select
                    value={detailForm.submittal_type}
                    onChange={(e) => setDetailForm((f) => ({ ...f, submittal_type: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    {SUBMITTAL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Status
                  <select
                    value={detailForm.status}
                    onChange={(e) => setDetailForm((f) => ({ ...f, status: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Priority
                  <select
                    value={detailForm.priority}
                    onChange={(e) => setDetailForm((f) => ({ ...f, priority: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Discipline
                <input
                  value={detailForm.discipline}
                  onChange={(e) => setDetailForm((f) => ({ ...f, discipline: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Submitted by
                  <input
                    value={detailForm.submitted_by}
                    onChange={(e) => setDetailForm((f) => ({ ...f, submitted_by: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Ball in court
                  <input
                    value={detailForm.ball_in_court}
                    onChange={(e) => setDetailForm((f) => ({ ...f, ball_in_court: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Submitted date
                  <input
                    type="date"
                    value={detailForm.submitted_date}
                    onChange={(e) => setDetailForm((f) => ({ ...f, submitted_date: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Required date
                  <input
                    type="date"
                    value={detailForm.required_date}
                    onChange={(e) => setDetailForm((f) => ({ ...f, required_date: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
                <label style={{ fontSize: 13, color: '#374151' }}>
                  Received date
                  <input
                    type="date"
                    value={detailForm.received_date}
                    onChange={(e) => setDetailForm((f) => ({ ...f, received_date: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                </label>
              </div>
              <label style={{ fontSize: 13, color: '#374151' }}>
                Revision
                <input
                  value={detailForm.revision}
                  onChange={(e) => setDetailForm((f) => ({ ...f, revision: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                />
              </label>

              <div
                style={{
                  marginTop: 8,
                  paddingTop: 16,
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>Review</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <label style={{ fontSize: 13, color: '#374151' }}>
                    Review status
                    <select
                      value={detailForm.review_status}
                      onChange={(e) => setDetailForm((f) => ({ ...f, review_status: e.target.value }))}
                      style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                    >
                      {REVIEW_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ fontSize: 13, color: '#374151' }}>
                    Reviewer
                    <input
                      value={detailForm.reviewer}
                      onChange={(e) => setDetailForm((f) => ({ ...f, reviewer: e.target.value }))}
                      style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                    />
                  </label>
                </div>
                <label style={{ fontSize: 13, color: '#374151', display: 'block', marginTop: 12 }}>
                  Review comments
                  <textarea
                    value={detailForm.review_comments}
                    onChange={(e) => setDetailForm((f) => ({ ...f, review_comments: e.target.value }))}
                    rows={3}
                    style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical' }}
                  />
                </label>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                  Review date is set automatically to today when you save.
                  {detailForm.review_date ? ` Last saved: ${detailForm.review_date}` : ''}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => handleViewFile(detailId)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  View attached file
                </button>
              </div>
            </div>
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => handleDeleteOne(detailId)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Delete
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  disabled={savingDetail}
                  onClick={() => setDetailId(null)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={savingDetail}
                  onClick={handleSaveDetail}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    cursor: savingDetail ? 'wait' : 'pointer',
                    fontWeight: 500,
                  }}
                >
                  {savingDetail ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmittalsDashboard;
